/**
 * Shared field primitives for the add-tile config sections (S6). Sections
 * receive `{ values, set }` — the dialog owns the TanStack form; `set`
 * patches field values through it so reset/submit stay centralized.
 */
import type { ReactNode } from 'react';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ColumnTypeInfo } from '../../../charts/types/chartTypes';
import type { AddTileFormValues } from './addTileFormModel';

export type SectionProps = {
  values: AddTileFormValues;
  set: (patch: Partial<AddTileFormValues>) => void;
  /** Column shape from the last preview (empty until one ran). */
  columnInfos: ColumnTypeInfo[];
};

export function FieldRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[92px_1fr] items-center gap-2">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

const NONE = '__none__';
/** Placeholder sentinel — Radix rejects an empty SelectItem value. */
const NO_COLUMNS = '__no_columns__';

/**
 * Copy for both the closed trigger and the open list while the preview
 * hasn't produced a column shape yet — an unexplained blank dropdown reads
 * as broken (S24 polish).
 */
export const NO_COLUMNS_HINT = 'Run Preview to list columns';

/**
 * Column dropdown fed by the preview's shape. `categories` narrows the
 * offering (e.g. numeric-only for values); the full list stays reachable via
 * the "All columns" group so a miscategorized column never blocks the user.
 */
export function ColumnSelect({
  value,
  onChange,
  columnInfos,
  categories,
  placeholder = 'Select column',
  allowNone = false,
  testId,
}: {
  value: string;
  onChange: (column: string) => void;
  columnInfos: ColumnTypeInfo[];
  categories?: Array<ColumnTypeInfo['category']>;
  placeholder?: string;
  allowNone?: boolean;
  testId?: string;
}) {
  const preferred = categories
    ? columnInfos.filter((c) => categories.includes(c.category))
    : columnInfos;
  const rest = categories ? columnInfos.filter((c) => !categories.includes(c.category)) : [];
  const noColumns = columnInfos.length === 0;
  return (
    <Select
      value={value || (allowNone ? NONE : '')}
      onValueChange={(v) => onChange(v === NONE ? '' : v)}
    >
      <SelectTrigger className="h-7 text-xs" data-testid={testId}>
        <SelectValue placeholder={noColumns ? NO_COLUMNS_HINT : placeholder} />
      </SelectTrigger>
      <SelectContent>
        {noColumns && (
          <SelectItem value={NO_COLUMNS} disabled data-testid="column-select-no-columns">
            {NO_COLUMNS_HINT}
          </SelectItem>
        )}
        {allowNone && <SelectItem value={NONE}>None</SelectItem>}
        {preferred.map((c) => (
          <SelectItem key={c.key} value={c.key}>
            <span className="truncate">{c.name}</span>
            <span className="ml-1 text-xs text-muted-foreground">({c.category})</span>
          </SelectItem>
        ))}
        {rest.map((c) => (
          <SelectItem key={c.key} value={c.key}>
            <span className="truncate opacity-70">{c.name}</span>
            <span className="ml-1 text-xs text-muted-foreground">({c.category})</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function EnumSelect<T extends string>({
  value,
  onChange,
  options,
  testId,
}: {
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: string }>;
  testId?: string;
}) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as T)}>
      <SelectTrigger className="h-7 text-xs" data-testid={testId}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
